import { BaseCheck, CheckAttributeMatch, CheckName } from 'dashboard/models/check/check';
import {
  AttributeComparison,
  ComparisonInputResult,
} from 'dashboard/models/check-result/check-requirement';
import { RiskData } from 'dashboard/models/verification-check';
import { GovernmentIdClass, GovernmentIdDesignation } from 'lib/constants/governmentIds';
import { CountryCode, USState } from 'lib/constants/locales';

export type IdAamvaDatabaseLookup = BaseCheck<
  CheckName.IdAamvaDatabaseLookup,
  never,
  'insufficient_match',
  | 'aamva_server_error'
  | 'invalid_country'
  | 'invalid_id_type'
  | 'invalid_issuing_authority'
  | 'missing_required_attributes'
  | 'missing_barcode_extraction'
  | 'not_enabled', // deprecated
  {
    'aamva-match-results': {
      'document-identification-number'?: CheckAttributeMatch;
      'name-first'?: CheckAttributeMatch;
      'name-last'?: CheckAttributeMatch;
      birthdate?: CheckAttributeMatch;
    };
  }
>;

export type IdAccountComparison = BaseCheck<
  CheckName.IdAccountComparison,
  never,
  'property_conflict' | 'face_mismatch' | 'unsupported_comparison_language',
  'no_account' | 'missing_properties',
  {
    'attribute-comparisons'?: AttributeComparison[];
    risks?: RiskData[];
  }
>;

export type IdAgeComparison = BaseCheck<
  CheckName.IdAgeComparison,
  never,
  'failed_requirement',
  'missing_birthdate' | 'no_configuration',
  {
    'country-code': CountryCode;
    age?: number;
    'min-age'?: number | null;
    'max-age'?: number | null;
  }
>;

export type IdAgeInconsistencyDetection = BaseCheck<
  CheckName.IdAgeInconsistencyDetection,
  never,
  'inconsistent_age',
  'missing_age_range' | 'missing_birthdate' | 'missing_face' | 'missing_issuing_date',
  {
    age: number;
    'predicted-age-range': {
      high: number;
      low: number;
    };
    risks?: RiskData[];
  }
>;

export type IdAttributeComparison = BaseCheck<
  CheckName.IdAttributeComparison,
  never,
  'property_conflict',
  'missing_properties' | 'unsupported_comparison_language',
  {
    'comparison-input-results'?: ComparisonInputResult[];
  }
>;

export type IdBarcodeDetection = BaseCheck<
  CheckName.IdBarcodeDetection,
  never,
  'barcode_not_decoded' | 'barcode_not_detected' | 'back_missing',
  'barcode_not_required' | 'unsupported_country',
  {
    eyes?: string;
    hair?: string;
    height?: string;
    sex?: string;
    weight?: string;
    'vehicle-class'?: string;
    'expiration-date'?: string;
    'issue-date'?: string;
    'identification-number'?: string;
    birthdate?: string;
    'name-first'?: string;
    'name-last'?: string;
  }
>;

export type IdBarcodeInconsistencyDetection = BaseCheck<
  CheckName.IdBarcodeInconsistencyDetection,
  never,
  | 'format_invalid'
  | 'required_field_missing'
  | 'field_invalid'
  | 'issuer_invalid'
  | 'issuer_mismatch'
  | 'revision_invalid'
  | 'metadata_invalid',
  'back_missing' | 'barcode_not_decoded' | 'barcode_not_detected' | 'unsupported_country',
  {
    // AAMVA Inconsistency is passed all the way from Scanner.
    'aamva-inconsistency-info'?: AamvaInconsistencyInfo;
    version?: string;
    'issuer-number'?: string;
    'issuer-code'?: string;
    'issuer-name'?: string;
  }
>;

interface AamvaInconsistencyInfo {
  'address-state-code'?: string;
  'address-state'?: string;
  field?: string;
  'issue-date'?: string;
  'missing-ids'?: string[];
  value?: string;
}

export type IdBlurDetection = BaseCheck<
  CheckName.IdBlurDetection,
  never,
  'too_blurry',
  never,
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdColorInconsistencyDetection = BaseCheck<
  CheckName.IdColorInconsistencyDetection,
  never,
  'too_few_colors' | 'image_is_monochrome',
  never,
  {
    risks?: RiskData[];
  }
>;

export type IdCompromisedDetection = BaseCheck<
  CheckName.IdCompromisedDetection,
  never,
  'fraudulent' | 'publicly_available' | 'sample_document',
  never,
  | {
      // NOTE: matched-compromised-image is legacy, fraudulent and publicly-available
      // matches will show up in the matches array
      'matched-compromised-image': string | null;
      // When sample-word key is present, sample words were detected
      // However, sample-word may still be nil if the word is sensitive.
      'sample-word': string | null;
    }
  | {
      // Match(es) found in the fraudulent / publicly available registry
      matches: CompromisedMatch[];
    }
  | Record<string, never>
>;

export type CompromisedMatch = {
  'document-front-photo-url': string | null;
  'document-back-photo-url': string | null;
  'document-token': string;
  'attributes-of-interest': string[];
  'attribute-set-matched-name': string;
  'risk-level': string;
};

export type IdCornerDetection = BaseCheck<
  CheckName.IdCornerDetection,
  never,
  'insufficient_padding' | 'unsupported_country' | 'unsupported_document_type' | 'unidentified',
  never,
  never
>;

export type IdDisallowedCountryDetection = BaseCheck<
  CheckName.IdDisallowedCountryDetection,
  never,
  'no_country' | 'not_allowed_by_configuration' | 'selected_country_mismatch',
  'no_country_configuration',
  {
    'country-code': CountryCode;
    // Old versions of this do not have 'enabled-country-codes' in the metadata
    'enabled-country-codes'?: CountryCode[];
    'selected-country-code': CountryCode;
  }
>;

export type IdDisallowedTypeDetection = BaseCheck<
  CheckName.IdDisallowedTypeDetection,
  never,
  | 'disallowed_designation'
  | 'not_allowed_by_configuration'
  | 'restricted_type'
  | 'type_mismatch'
  | 'type_not_detected',
  'unrestricted',
  {
    'selected-id-classes': GovernmentIdClass[];
    'detected-id-class': string;
    'enabled-id-classes': GovernmentIdClass[];
    'detected-id-designations': GovernmentIdDesignation[];
    'disallowed-id-designations': GovernmentIdDesignation[];
    'country-code': CountryCode;
  }
>;

export type IdDoubleSideDetection = BaseCheck<
  CheckName.IdDoubleSideDetection,
  never,
  'side_submitted_twice',
  'single_sided',
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdElectronicReplicaDetection = BaseCheck<
  CheckName.IdElectronicReplicaDetection,
  never,
  | 'digital_icon_detected'
  | 'digital_text_found'
  | 'emulator_detected'
  | 'photocopy_found'
  | 'replica_detected'
  | 'rooted_device'
  | 'screenshot_found'
  | 'virtual_camera_detected',
  never,
  {
    risks?: RiskData[];
  }
>;

export type IdEntityDetection = BaseCheck<
  CheckName.IdEntityDetection,
  never,
  'cannot_verify_id',
  never,
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdExperimentalModelDetection = BaseCheck<
  CheckName.IdExperimentalModelDetection,
  never,
  'match_detected',
  'not_enabled',
  {
    'matched-models': Array<{
      'model-code': string;
      'version-code': string;
      threshold: number;
      'risk-level': string;
    }>;
  }
>;

export type IdExpiredDetection = BaseCheck<
  CheckName.IdExpiredDetection,
  never,
  'expired',
  'no_expiration_date',
  {
    'submitted-date'?: string;
    'expiration-date'?: string;
    'accepted-until-date'?: string;
    'grace-period-days'?: number;
  }
>;

export type IdExtractedPropertiesDetection = BaseCheck<
  CheckName.IdExtractedPropertiesDetection,
  'required_properties_absent_on_id',
  'property_not_extracted' | 'name_partially_extracted',
  'no_required_properties',
  {
    // Started sending required-properties on 2020-09-22
    'required-properties'?: RequiredProperty[];
    'missing-required-properties'?: RequiredProperty[];
    'absent-properties'?: AbsentProperty[];
    birthdate: string | null;
    'identification-number': string | null;
    'name-first': string | null;
    'name-last': string | null;
    'address-street-1': string | null;
    'address-street-2': string | null;
    'address-city': string | null;
    'address-subdivision': string | null;
    'address-postal-code': string | null;
    'issue-date': string | null;
    'expiration-date': string | null;
    'document-number': string | null;
  }
>;

export enum RequiredProperty {
  Birthdate = 'birthdate',
  IdentificationNumber = 'identification_number',
  NameFirst = 'name_first',
  NameLast = 'name_last',
  AddressStreet1 = 'address_street_1',
  AddressStreet2 = 'address_street_2',
  AddressCity = 'address_city',
  AddressSubdivision = 'address_subdivision',
  AddressPostalCode = 'address_postal_code',
  IssueDate = 'issue_date',
  ExpirationDate = 'expiration_date',
  DocumentNumber = 'document_number',
}

enum AbsentFieldReason {
  AlwaysAbsent = 'always_absent',
  SeniorId = 'senior_id',
}

interface AbsentProperty {
  field: RequiredProperty;
  reason: AbsentFieldReason;
}

export type IdExtractionInconsistencyDetection = BaseCheck<
  CheckName.IdExtractionInconsistencyDetection,
  never,
  'property_mismatch',
  'back_missing' | 'missing_properties' | 'unsupported_country',
  {
    'attribute-comparisons'?: AttributeComparison[];
  }
> & {
  // TODO(lucas): Figure out if we can just -n this.
  'legacy-debug'?:
    | {
        'fields-from-layout': {
          'issuing-country'?: CountryCode;
        } & Record<string, string>;
        'fields-from-barcode': {
          'issuing-country'?: CountryCode;
        } & Record<string, string>;
      }
    | Record<string, never>; // TODO: fix this, not sure if accurate
};

export type IdFabricationDetection = BaseCheck<
  CheckName.IdFabricationDetection,
  never,
  'camouflage_passport' | 'fabricated_id_version',
  never,
  { risks?: RiskData[] }
>;

export type IdGlareDetection = BaseCheck<
  CheckName.IdGlareDetection,
  never,
  'too_much_glare',
  never,
  Record<string, never> // TODO: fix this, not sure if accuratenever
>;

export type IdHandwritingDetection = BaseCheck<
  CheckName.IdHandwritingDetection,
  never,
  'handwriting_found',
  never,
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdInconsistentRepeatDetection = BaseCheck<
  CheckName.IdInconsistentRepeatDetection,
  never,
  'inconsistent_details' | 'inconsistent_face',
  'low_quality_portrait' | 'not_enabled' | 'no_portrait',
  {
    matches: GovernmentIdInconsistentMatch[];
  }
>;

export type GovernmentIdInconsistentMatch = {
  'attributes-mismatched': string[];
  'document-front-photo-url': string | null;
  'document-back-photo-url': string | null;
  'verification-id': string | null;
  'inquiry-id': string | null;
  'inquiry-status': string | null;
};

export type IdInquiryComparison = BaseCheck<
  CheckName.IdInquiryComparison,
  never,
  'names_swapped' | 'property_conflict',
  'unsupported_comparison_language' | 'missing_properties' | 'no_inquiry',
  {
    'attribute-comparisons'?: AttributeComparison[];
  }
> & {
  'legacy-debug'?: {
    verification: IdInquiryComparisonIdentityRecord;
    inquiry: IdInquiryComparisonIdentityRecord;
    'claimed-attributes': IdInquiryComparisonIdentityRecord;
  };
};

interface IdInquiryComparisonIdentityRecord {
  token?: string;
  name_first?: string;
  name_last?: string;
  birthdate?: string;
  address_street_1?: string;
  address_street_2?: string;
  address_city?: string;
  address_subdivision?: string;
  address_postal_code?: string;
}

export type IdMrzDetection = BaseCheck<
  CheckName.IdMrzDetection,
  never,
  'mrz_not_found' | 'mrz_incomplete',
  never,
  {
    // fields from mrz
    'issuing-country'?: CountryCode;
  } & Record<string, string | null | undefined>
>;

export type IdMrzInconsistencyDetection = BaseCheck<
  CheckName.IdMrzInconsistencyDetection,
  never,
  'mrz_checksum_invalid',
  'mrz_not_found' | 'mrz_not_verifiable',
  {
    'mrz-invalid-fields': {
      field: string;
      'parsed-value'?: string;
      'parsed-checksum'?: string;
      'calculated-checksum'?: string;
    }[];
  }
>;

export type IdNumberFormatInconsistencyDetection = BaseCheck<
  CheckName.IdNumberFormatInconsistencyDetection,
  never,
  'incorrect_format' | 'invalid_checksum' | 'invalid_encoded_data',
  'unsupported_country',
  {
    'identification-number'?: string;
    'country-code'?: CountryCode;
    'detected-id-class'?: GovernmentIdClass;
    'id-number-name'?: string;
  }
>;

export type IdPaperDetection = BaseCheck<
  CheckName.IdPaperDetection,
  never,
  'paper_id_detected',
  never,
  {
    risks?: RiskData[];
    'photocopy-detected'?: boolean;
    'lower-confidence-region'?: boolean;
  }
>;

export type IdPhysicalTamperDetection = BaseCheck<
  CheckName.IdPhysicalTamperDetection,
  'tamper_detected',
  never,
  never,
  {
    'risk-level'?: string;
    risks?: RiskData[];
  }
>;

export type IdPoBoxDetection = BaseCheck<
  CheckName.IdPoBoxDetection,
  'address_is_hc_box',
  'address_is_po_box',
  'missing_address',
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdPortraitClarityDetection = BaseCheck<
  CheckName.IdPortraitClarityDetection,
  never,
  'portrait_not_clear',
  never,
  {
    risks?: RiskData[];
  }
>;

export type IdPortraitDetection = BaseCheck<
  CheckName.IdPortraitDetection,
  never,
  'portrait_not_found',
  never,
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdPublicFigureDetection = BaseCheck<
  CheckName.IdPublicFigureDetection,
  never,
  'public_figure_detected',
  never,
  {
    'public-figure-name'?: string;
    risks?: RiskData[];
  }
>;

export type IdRealIdDetection = BaseCheck<
  CheckName.IdRealIdDetection,
  never,
  | 'federal_limits_undetected'
  | 'invalid_country'
  | 'invalid_id_type'
  | 'invalid_barcode'
  | 'no_barcode',
  never,
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdRepeatDetection = BaseCheck<
  CheckName.IdRepeatDetection,
  never,
  'repeat',
  'not_enabled',
  {
    matches: RepeatMatch[];
  }
>;

export type RepeatMatch = {
  'attributes-matched': string[];
  'document-front-photo-url': string;
  'document-back-photo-url': string;
  'verification-id': string;
  'inquiry-id': string;
  'inquiry-status': string;
};

export type IdSelfieComparison = BaseCheck<
  CheckName.IdSelfieComparison,
  never,
  'face_mismatch',
  'no_selfie',
  {
    risks?: RiskData[];
  }
>;

export type IdTamperDetection = BaseCheck<
  CheckName.IdTamperDetection,
  'portrait_tampering',
  'exif_inconsistency' | 'image_manipulated',
  never,
  {
    'invalid-exif-tag-value'?: string;
    'editor-used'?: string;
    risks?: RiskData[];
  }
>;

export type IdUnprocessableSubmissionDetection = BaseCheck<
  CheckName.IdUnprocessableSubmissionDetection,
  never,
  'age_below_limit' | 'submission_invalid',
  never,
  Record<string, never> // TODO: fix this, not sure if accurate
>;

export type IdValidDatesDetection = BaseCheck<
  CheckName.IdValidDatesDetection,
  never,
  'issuer_rules_inconsistency',
  'unknown_date_rules',
  {
    'issuing-country': CountryCode;
    'issuing-subdivision': USState;
  }
>;

export type IdVideoQualityDetection = BaseCheck<
  CheckName.IdVideoQualityDetection,
  never,
  'not_found' | 'corrupt' | 'low_bit_rate' | 'low_frame_rate' | 'duration_too_short',
  never,
  {
    'bit-rate': number;
    'content-type': string;
    'frame-rate': number;
  }
>;

export type IdDamagedDetection = BaseCheck<
  CheckName.IdDamagedDetection,
  never,
  'holepunch_detected',
  never,
  never
>;
